/**
 * Idempotency keys for the client's POST requests. Pure (no transport, no Telegram), so the
 * Mini App and the studio website share it (shared-client-screens D2); api/client re-exports it.
 */

/**
 * Generate an idempotency key for POST requests.
 * Callers should generate this ONCE per user action (not inside mutation).
 *
 * Uses crypto.randomUUID() where available, with fallback for older webviews.
 */
export function generateIdempotencyKey(): string {
  // Modern browsers support crypto.randomUUID()
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback for older embedded webviews (e.g., older Android WebView)
  // Uses crypto.getRandomValues() which has broader support
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    // Set version (4) and variant (RFC 4122) bits
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    // Convert to hex string with dashes
    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // If all else fails, throw a clear error
  throw new Error(
    'Unable to generate idempotency key: crypto API not available. ' +
      'This browser/webview may be too old to support secure booking.',
  );
}
